
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowDown, ArrowUp } from 'lucide-react';

// Sample comparison data based on HVAC and chillers
const consumptionComparisonData = [
  { name: 'Jan', current: 5200, previous: 6100 },
  { name: 'Feb', current: 4900, previous: 5800 },
  { name: 'Mar', current: 5100, previous: 5600 },
  { name: 'Apr', current: 4800, previous: 5500 },
  { name: 'May', current: 4600, previous: 5200 },
  { name: 'Jun', current: 5300, previous: 5700 },
  { name: 'Jul', current: 5800, previous: 6000 },
  { name: 'Aug', current: 5500, previous: 5900 },
  { name: 'Sep', current: 5100, previous: 5600 },
  { name: 'Oct', current: 4900, previous: 5400 },
  { name: 'Nov', current: 5300, previous: 5800 },
  { name: 'Dec', current: 5600, previous: 6200 },
];

// Calculate total savings
const calculateSavings = () => {
  const currentTotal = consumptionComparisonData.reduce((acc, item) => acc + item.current, 0);
  const previousTotal = consumptionComparisonData.reduce((acc, item) => acc + item.previous, 0);
  const savings = previousTotal - currentTotal;
  const savingsPercentage = (savings / previousTotal) * 100;
  return { savings, savingsPercentage: savingsPercentage.toFixed(1) };
};

const TotalConsumptionsComparison = () => {
  const { savings, savingsPercentage } = calculateSavings();

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-xl font-bold">Consumption Comparison (kWh)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row md:justify-between mb-4">
          <div className="flex gap-4 mb-4 md:mb-0">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
              <span className="text-sm">Current Year</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-gray-300 mr-2"></div>
              <span className="text-sm">Previous Year</span>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md flex items-center">
            <ArrowDown className="text-green-600 h-5 w-5 mr-2" />
            <div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Savings:</span>
              <span className="text-lg font-bold text-green-600 ml-2">{savingsPercentage}%</span>
              <span className="text-sm text-gray-500 ml-2">({savings.toLocaleString()} kWh)</span>
            </div>
          </div>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={consumptionComparisonData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip
                formatter={(value) => [`${value.toLocaleString()} kWh`, value === consumptionComparisonData[0].current ? 'Current Year' : 'Previous Year']}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Bar dataKey="current" fill="#3b82f6" name="Current Year" radius={[4, 4, 0, 0]} />
              <Bar dataKey="previous" fill="#d1d5db" name="Previous Year" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default TotalConsumptionsComparison;
